import type { Node as PMNode, ResolvedPos } from '@atlaskit/editor-prosemirror/model';

export type SuggestionCardAnchorRange = { endPos: number; startPos: number };

export type SuggestionCardAnchorRanges = {
	/** Range to anchor to when `primary` resolves to nothing, e.g. because a diff hides it. */
	fallback?: SuggestionCardAnchorRange;
	/** Closest range to the target; the one the card lines up with whenever it renders. */
	primary?: SuggestionCardAnchorRange;
};

/**
 * Whether `node`'s DOM element can hold a stray anchor span directly. Structural containers
 * (`table`, `tableRow`, `layoutSection`, a list) can't — the browser reparents it into an
 * anonymous row/cell (EDITOR-8442) or an extra grid item.
 */
function canHostAnchor(node: PMNode): boolean {
	return (
		node.type.inlineContent ||
		node.type.contentMatch.matchType(node.type.schema.nodes.paragraph) !== null
	);
}

/**
 * The position within `range` closest to its `edge` whose parent can host an anchor widget,
 * walking one token at a time. `undefined` when the whole range is unhostable.
 */
function toHostableAnchorPos(
	doc: PMNode,
	range: { end: number; start: number },
	edge: 'end' | 'start',
): number | undefined {
	const step = edge === 'start' ? 1 : -1;

	for (
		let pos = edge === 'start' ? range.start : range.end;
		pos >= range.start && pos <= range.end;
		pos += step
	) {
		if (canHostAnchor(doc.resolve(pos).parent)) {
			return pos;
		}
	}

	return undefined;
}

/**
 * The boundary of the node at `fromDepth`, moved outward while the enclosing node can't host an
 * anchor widget. Always resolves — at worst to the target's own top-level block, since the doc
 * itself always can.
 */
function toOutwardAnchorRange($pos: ResolvedPos, fromDepth: number): SuggestionCardAnchorRange {
	let depth = fromDepth;
	while (depth > 1 && !canHostAnchor($pos.node(depth - 1))) {
		depth--;
	}
	return { startPos: $pos.before(depth), endPos: $pos.after(depth) };
}

/**
 * The target node's own outer boundary — what `main.tsx` falls back to when this module has
 * nothing more specific to offer.
 */
function ownNodeBoundaryRange(doc: PMNode, pos: number): SuggestionCardAnchorRange | undefined {
	const node = doc.nodeAt(pos);
	return node ? { startPos: pos, endPos: pos + node.nodeSize } : undefined;
}

/**
 * Pairs a primary range with its fallback, dropping the fallback if it's identical to the
 * primary, and promoting it to `primary` if there's no primary at all.
 */
function withOutwardFallback(
	primary: SuggestionCardAnchorRange | undefined,
	outward: SuggestionCardAnchorRange,
): SuggestionCardAnchorRanges {
	if (!primary) {
		return { primary: outward };
	}
	return primary.startPos === outward.startPos && primary.endPos === outward.endPos
		? { primary }
		: { primary, fallback: outward };
}

/**
 * The `primary`/`fallback` pair ignoring any enclosing expand. Split out so
 * `findSuggestionCardAnchorRanges` can run it first and then substitute its own fallback once it
 * knows whether an expand is involved.
 */
function findContentAnchorRanges(
	doc: PMNode,
	$pos: ResolvedPos,
	pos: number,
	tableCellDepth: number | undefined,
): SuggestionCardAnchorRanges {
	// `$pos.parent` is the target's own parent, since `pos` sits right before it. When that
	// parent can't host a widget (a `<tr>` between cells, a `<table>` between rows, a layout
	// between columns), the target's own boundary is unusable and we have to reach into its
	// content instead.
	if (!canHostAnchor($pos.parent)) {
		const nodeAtPos = doc.nodeAt(pos);
		const innerRange = nodeAtPos
			? { start: pos + 1, end: pos + nodeAtPos.nodeSize - 1 }
			: undefined;
		const innerStartPos = innerRange && toHostableAnchorPos(doc, innerRange, 'start');
		const innerEndPos = innerRange && toHostableAnchorPos(doc, innerRange, 'end');

		// Reaching into the target's own content is preferred, but that's also the range a diff
		// can hide, hence the outward fallback.
		return withOutwardFallback(
			innerStartPos !== undefined && innerEndPos !== undefined
				? { startPos: innerStartPos, endPos: innerEndPos }
				: undefined,
			toOutwardAnchorRange($pos, $pos.depth),
		);
	}

	if (tableCellDepth === undefined) {
		return {};
	}

	// `$pos.parent` already passed `canHostAnchor`, so the target's own boundary is always safe
	// to anchor at directly — pinning to the whole cell instead would swallow untouched sibling
	// content. The fallback (for when the target itself is diff-hidden) reaches into the cell's
	// own content bounds instead of escalating past the cell/row all the way to the table.
	return withOutwardFallback(ownNodeBoundaryRange(doc, pos), {
		startPos: $pos.start(tableCellDepth),
		endPos: $pos.end(tableCellDepth),
	});
}

/**
 * Pins the suggestion card's anchor spans to a meaningful enclosing block instead of the deep
 * inner target node. `primary` is the closest usable range; `fallback` is an outward range for
 * when `primary` sits in content `editor-plugin-show-diff` hides with `display: none`, which the
 * consumer chains via `anchor()`'s fallback argument.
 */
export function findSuggestionCardAnchorRanges(
	doc: PMNode,
	pos: number,
): SuggestionCardAnchorRanges {
	const $pos = doc.resolve(pos);
	let outermostExpandDepth: number | undefined;
	let tableCellDepth: number | undefined;

	for (let depth = $pos.depth; depth > 0; depth--) {
		const typeName = $pos.node(depth).type.name;
		if (typeName === 'expand' || typeName === 'nestedExpand') {
			outermostExpandDepth = depth;
		} else if (
			(typeName === 'tableCell' || typeName === 'tableHeader') &&
			tableCellDepth === undefined
		) {
			tableCellDepth = depth;
		}
	}

	const contentRanges = findContentAnchorRanges(doc, $pos, pos, tableCellDepth);

	if (outermostExpandDepth === undefined) {
		return contentRanges;
	}

	// `contentRanges.primary` may be empty (falls back to `ownNodeBoundaryRange`, matching what
	// `main.tsx` does outside this module), but it's already as close to the target's content as
	// it gets. An expand's body is `display: none` while collapsed, hiding that content anchor
	// entirely, so its own outer boundary — always rendered — replaces whatever fallback
	// `contentRanges` computed.
	return withOutwardFallback(contentRanges.primary ?? ownNodeBoundaryRange(doc, pos), {
		startPos: $pos.before(outermostExpandDepth),
		endPos: $pos.after(outermostExpandDepth),
	});
}
